import { SilentCronXError } from "../utils/errors.js";

type CronField = Set<number>;

export type ParsedCron = {
  hasSeconds: boolean;
  seconds: CronField;
  minutes: CronField;
  hours: CronField;
  daysOfMonth: CronField;
  months: CronField;
  daysOfWeek: CronField;
  expression: string;
};

const MONTHS: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const DOW: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

export function parseCronExpression(expression: string): ParsedCron {
  const parts = expression.trim().replace(/\s+/g, " ").split(" ");
  if (parts.length !== 5 && parts.length !== 6) {
    throw new SilentCronXError("Invalid cron expression. Expected 5 fields, or 6 fields with seconds.");
  }

  const hasSeconds = parts.length === 6;
  const [secondsRaw, minutesRaw, hoursRaw, domRaw, monthsRaw, dowRaw] = hasSeconds
    ? parts
    : ["0", ...parts];

  return {
    hasSeconds,
    seconds: parseField(secondsRaw, 0, 59),
    minutes: parseField(minutesRaw, 0, 59),
    hours: parseField(hoursRaw, 0, 23),
    daysOfMonth: parseField(domRaw, 1, 31),
    months: parseField(monthsRaw, 1, 12, MONTHS),
    daysOfWeek: normalizeDow(parseField(dowRaw, 0, 7, DOW)),
    expression,
  };
}

export function validateCronExpression(expression: string): void {
  parseCronExpression(expression);
}

export function getNextCronDate(expression: string | ParsedCron, from = new Date(), timezone = "UTC"): Date {
  const parsed = typeof expression === "string" ? parseCronExpression(expression) : expression;
  const stepMs = parsed.hasSeconds ? 1000 : 60_000;
  let candidate = new Date(Math.ceil((from.getTime() + stepMs) / stepMs) * stepMs);
  const limit = candidate.getTime() + 366 * 24 * 60 * 60 * 1000;

  while (candidate.getTime() <= limit) {
    const parts = getZonedParts(candidate, timezone);
    if (
      parsed.seconds.has(parts.second) &&
      parsed.minutes.has(parts.minute) &&
      parsed.hours.has(parts.hour) &&
      parsed.daysOfMonth.has(parts.day) &&
      parsed.months.has(parts.month) &&
      parsed.daysOfWeek.has(parts.weekday)
    ) {
      return candidate;
    }
    candidate = new Date(candidate.getTime() + stepMs);
  }

  throw new SilentCronXError(`Unable to find next run for cron expression: ${parsed.expression}`);
}

function parseField(raw: string | undefined, min: number, max: number, aliases: Record<string, number> = {}): CronField {
  if (!raw) throw new SilentCronXError("Cron field is missing");
  const values = new Set<number>();
  for (const token of raw.toLowerCase().split(",")) {
    const [rangeRaw, stepRaw] = token.split("/");
    const step = stepRaw ? Number(stepRaw) : 1;
    if (!Number.isInteger(step) || step <= 0) {
      throw new SilentCronXError(`Invalid cron step: ${token}`);
    }

    let start: number;
    let end: number;
    if (rangeRaw === "*") {
      start = min;
      end = max;
    } else if (rangeRaw?.includes("-")) {
      const [a, b] = rangeRaw.split("-");
      start = parseValue(a, aliases);
      end = parseValue(b, aliases);
    } else {
      start = parseValue(rangeRaw, aliases);
      end = start;
    }

    if (start < min || end > max || start > end) {
      throw new SilentCronXError(`Cron value out of range: ${token}`);
    }
    for (let value = start; value <= end; value += step) values.add(value);
  }
  return values;
}

function parseValue(raw: string | undefined, aliases: Record<string, number>): number {
  if (!raw) throw new SilentCronXError("Invalid empty cron value");
  const aliased = aliases[raw] ?? Number(raw);
  if (!Number.isInteger(aliased)) throw new SilentCronXError(`Invalid cron value: ${raw}`);
  return aliased;
}

function normalizeDow(values: CronField): CronField {
  const normalized = new Set<number>();
  for (const value of values) normalized.add(value === 7 ? 0 : value);
  return normalized;
}

function getZonedParts(date: Date, timezone: string): {
  second: number;
  minute: number;
  hour: number;
  day: number;
  month: number;
  weekday: number;
} {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    second: "numeric",
    minute: "numeric",
    hour: "numeric",
    hourCycle: "h23",
    day: "numeric",
    month: "numeric",
    weekday: "short",
  });
  const map = new Map(formatter.formatToParts(date).map((part) => [part.type, part.value]));
  return {
    second: Number(map.get("second")),
    minute: Number(map.get("minute")),
    hour: Number(map.get("hour")),
    day: Number(map.get("day")),
    month: Number(map.get("month")),
    weekday: DOW[(map.get("weekday") ?? "sun").toLowerCase()] ?? 0,
  };
}
